import clsx from 'clsx';
import React from 'react';
import { HeadingContext } from 'react-aria-components';

import { ButtonSkeleton } from '../Button';
import { ListSkeleton } from '../List';
import { TextSkeleton } from '../Skeleton';
import { type IconDefinition, renderIcon } from '../util';

/**
 * Indicate the level of the labeled container.
 * 1 is the highest level, a primary container.
 * 2 and 3 are the lowest levels, a secondary container.
 *
 * @default 1
 */
export type LabeledContainerLevel = 1 | 2 | 3;

export type LabeledContainerLevelContextType = 0 | LabeledContainerLevel;

export const LabeledContainerLevelContextDefault: LabeledContainerLevelContextType = 0;

export const LabeledContainerLevelContext = React.createContext<LabeledContainerLevelContextType>(
    LabeledContainerLevelContextDefault
);

const levelToHeading: Record<LabeledContainerLevel, { level: number; className: string }> = {
    1: { level: 2, className: 'text-ui-heading-small text-base' }, // This one is styled like a <Label />
    2: { level: 2, className: 'text-muted font-normal text-ui-base' },
    3: { level: 3, className: 'text-muted text-ui-small' },
};

export type LabeledContainerProps = {
    children: React.ReactNode;
    className?: string;
    level?: LabeledContainerLevel;
};

/**
 * Section container labeled.
 * Do not use it around form elements, and instead use `FieldGroup` for it.
 *
 * @example
 * <LabeledContainer>
 *   <LabeledContainerHeader>
 *     <LabeledContainerActionableTitle>
 *       <Heading>Label</Heading>
 *       <Button label="Action" />
 *     </LabeledContainerActionableTitle>
 *   </LabeledContainerHeader>
 *   <LabeledContainerBody>
 *     <div>Content</div>
 *   </LabeledContainerBody>
 * </LabeledContainer>
 */
export function LabeledContainer(props: LabeledContainerProps) {
    const parentLevel = React.useContext(LabeledContainerLevelContext);
    const { children, className, level = normalizeLevel(parentLevel + 1) } = props;

    return (
        <div className={clsx('flex flex-col', className, level > 1 ? 'gap-1.5' : 'gap-2')}>
            <LabeledContainerLevelContext.Provider value={level}>
                <HeadingContext.Provider value={levelToHeading[level]}>
                    {children}
                </HeadingContext.Provider>
            </LabeledContainerLevelContext.Provider>
        </div>
    );
}

/**
 * Provider to define the level to use for a current set of labeled containers.
 */
export function LabeledContainerLevelProvider(props: {
    children: React.ReactNode;
    level: LabeledContainerLevel;
}) {
    const { children, level } = props;

    return (
        <LabeledContainerLevelContext.Provider value={(level - 1) as 0 | LabeledContainerLevel}>
            {children}
        </LabeledContainerLevelContext.Provider>
    );
}

/**
 * Hook to get the level of the labeled container.
 */
export function useLabeledContainerLevel() {
    const currentLevel = React.useContext(LabeledContainerLevelContext);
    if (currentLevel === 0) {
        throw new Error(
            'useLabeledContainerLevel must be used within a LabeledContainerLevelProvider'
        );
    }
    return currentLevel;
}

export function LabeledContainerHeader(props: { children: React.ReactNode; className?: string }) {
    const { children, className } = props;

    return <div className={clsx('flex flex-col gap-0', className)}>{children}</div>;
}

/**
 * Wrap around the label of the section and an action button.
 * @example
 * <LabeledContainerActionableTitle>
 *   <Heading>Label</Heading>
 *   <Button label="Action" />
 * </LabeledContainerActionableTitle>
 */
export function LabeledContainerActionableTitle(props: {
    children: React.ReactNode;
    className?: string;
}) {
    const { children, className } = props;

    return (
        <div className={clsx('flex flex-row items-center justify-between gap-2', className)}>
            {children}
        </div>
    );
}

/**
 * Small icon displayed alongside the heading text inside a LabeledContainerHeader.
 * @example
 * <LabeledContainerHeader>
 *   <Heading>
 *     <LabeledContainerHeadingIcon><Icon.Warning /></LabeledContainerHeadingIcon>
 *     Issues
 *   </Heading>
 * </LabeledContainerHeader>
 */
export function LabeledContainerHeadingIcon(props: { children: IconDefinition }) {
    return renderIcon(props.children, { className: 'size-3.5 mr-1' });
}

export function LabeledContainerBody(props: { children: React.ReactNode; className?: string }) {
    const { children, className } = props;

    return <div className={clsx('flex flex-col gap-2', className)}>{children}</div>;
}

/**
 * Skeleton for a labeled container.
 */
export function LabeledContainerSkeleton(props: { className?: string }) {
    const { className } = props;

    return (
        <LabeledContainer className={className}>
            <LabeledContainerHeader>
                <LabeledContainerActionableTitle>
                    <TextSkeleton>Label</TextSkeleton>
                    <ButtonSkeleton label="Action" iconOnly />
                </LabeledContainerActionableTitle>
            </LabeledContainerHeader>
            <LabeledContainerBody>
                <ListSkeleton items={2} size="small" />
            </LabeledContainerBody>
        </LabeledContainer>
    );
}

function normalizeLevel(input: number): LabeledContainerLevel {
    return input <= 3 ? (input as LabeledContainerLevel) : 3;
}
